import React from 'react';

import isArray from 'lodash/isArray';

import { mdiFileTreeOutline } from '@lumapps/lumx/icons';
import { SkeletonTypography, Text, Typography } from '@lumapps/lumx/react';
import { Metadata } from '@lumapps/metadata/types';
import { useTranslate } from '@lumapps/translations';

import { DrawerSection, DrawerSectionProps } from '../DrawerSection';

export interface DrawerMetadatasSectionProps extends Omit<DrawerSectionProps, 'title'> {
    /** whether the metadatas are loading or not */
    isLoading?: boolean;
    /** list of parent metadatas to display */
    parentMetadatas: Partial<Metadata>[];
    /** list of selected metadata values for each metadata parent */
    selectedMetadatas: Record<string, Partial<Metadata> | Partial<Metadata>[]>;
}

/**
 * Displays a list of metadatas inside the context of a Drawer
 * @family Drawer
 * @param DrawerMetadatasSectionProps
 * @returns DrawerMetadatasSection
 */
export const DrawerMetadatasSection: React.FC<DrawerMetadatasSectionProps> = ({
    selectedMetadatas,
    parentMetadatas,
    isLoading,
    ...props
}) => {
    const { translateObject } = useTranslate();

    if (isLoading) {
        return (
            <DrawerSection
                title={<SkeletonTypography typography="overline" width="100px" />}
                icon={mdiFileTreeOutline}
                isLoading
                {...props}
            />
        );
    }

    return (
        <>
            {parentMetadatas.map((parentMetadata) => {
                const metadatas = selectedMetadatas[parentMetadata.id as string];

                if (metadatas) {
                    const metadataToUse = isArray(metadatas) ? metadatas : [metadatas];

                    if (metadataToUse.length > 0) {
                        return (
                            <DrawerSection
                                title={translateObject(parentMetadata.name) || ''}
                                key={parentMetadata.id}
                                icon={mdiFileTreeOutline}
                                {...props}
                            >
                                <Text as="span" typography={Typography.body1}>
                                    {metadataToUse?.map((metadata) => translateObject(metadata.name)).join(', ')}
                                </Text>
                            </DrawerSection>
                        );
                    }
                }

                return null;
            })}
        </>
    );
};
